import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { SocialFollow } from '@/components/ui/social-follow';
import logoFull from '@/assets/odyvoy-logo-full.png';
import { FloatingChatButton } from '@/components/chat/FloatingChatButton';
import { CheckoutConfirmationModal } from '@/components/checkout/CheckoutConfirmationModal';
import { PricingHeader } from '@/components/pricing/PricingHeader';
import { PricingHero } from '@/components/pricing/PricingHero';
import { PricingPlansSection } from '@/components/pricing/PricingPlansSection';
import { BoostPassesSection } from '@/components/pricing/BoostPassesSection';
import { BoostExitIntentPopup } from '@/components/pricing/BoostExitIntentPopup';
import { PricingComparison } from '@/components/pricing/PricingComparison';
import { GuestBoostModal } from '@/components/pricing/GuestBoostModal';
import { GiftSection } from '@/components/pricing/GiftSection';
import { PricingFAQ } from '@/components/pricing/PricingFAQ';
import { GiftModal, type GiftDetails, type GiftForm } from '@/components/pricing/GiftModal';
import { pricingPlans, pricingStructuredData } from '@/components/pricing/pricingData';

const Pricing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [isAnnual, setIsAnnual] = useState(false);
  const [hasReferralDiscount, setHasReferralDiscount] = useState(false);
  const [guestBoost, setGuestBoost] = useState<{
    open: boolean;
    tier: 'premium_economy' | 'first_class';
    duration: string;
    price: number;
  }>({ open: false, tier: 'premium_economy', duration: '3-Day', price: 2.99 });
  const [giftModalOpen, setGiftModalOpen] = useState(false);
  const [giftDetails, setGiftDetails] = useState<GiftDetails | null>(null);
  const [giftForm, setGiftForm] = useState<GiftForm>({
    recipientName: '',
    recipientEmail: '',
    senderName: '',
    message: '',
  });
  const [giftLoading] = useState(false);
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);
  const [pendingPurchase, setPendingPurchase] = useState<{
    type: 'subscription' | 'gift' | 'boost';
    tier: string;
    duration?: string;
    price: number;
    giftDetails?: { recipientName: string; recipientEmail: string; message: string };
  } | null>(null);

  useEffect(() => {
    const referralCode = localStorage.getItem('referral_code');
    if (referralCode) setHasReferralDiscount(true);

    const params = new URLSearchParams(window.location.search);
    if (params.get('promo') === 'launch50') {
      toast({
        title: '🎉 Launch Special Activated!',
        description: 'Use code LAUNCH50PE for Premium Economy or LAUNCH50FC for First Class to get 50% off your first month!',
        duration: 8000,
      });
    }
  }, [toast]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const giftSuccess = params.get('gift_success');
    const giftCancelled = params.get('gift_cancelled');
    const boostCancelled = params.get('boost_cancelled');
    const subscriptionCancelled = params.get('subscription_cancelled');
    const subscriptionSuccess = params.get('subscription_success');
    const sessionId = params.get('session_id');
    const completeGiftPayment = async () => {
      if (giftSuccess === 'true' && sessionId) {
        try {
          const { error } = await supabase.functions.invoke('complete-gift-payment', {
            body: { session_id: sessionId },
          });
          if (error) throw error;
          toast({ title: '🎉 Gift Sent Successfully!', description: 'The recipient will receive an email with instructions to redeem their gift subscription.' });
        } catch (error) {
          console.error('Error completing gift payment:', error);
          toast({ title: 'Gift Sent', description: 'Payment successful! The recipient will receive their gift details shortly.' });
        }
        window.history.replaceState({}, '', '/pricing');
      } else if (giftCancelled === 'true') {
        toast({ title: 'Payment Cancelled', description: 'Your gift subscription payment was cancelled. You can try again anytime.', variant: 'destructive' });
        window.history.replaceState({}, '', '/pricing');
      } else if (boostCancelled === 'true') {
        toast({ title: 'Payment Cancelled', description: 'Your boost pass purchase was cancelled. You can try again anytime.', variant: 'destructive' });
        window.history.replaceState({}, '', '/pricing');
      } else if (subscriptionCancelled === 'true') {
        toast({ title: 'Subscription Cancelled', description: 'Your subscription purchase was cancelled. You can try again anytime.', variant: 'destructive' });
        window.history.replaceState({}, '', '/pricing');
      } else if (subscriptionSuccess === 'true') {
        toast({ title: '🎉 Subscription Activated!', description: 'Your subscription is now active. Enjoy all the premium features!' });
        window.history.replaceState({}, '', '/pricing');
      }
    };
    completeGiftPayment();
  }, [toast]);

  const handleGetStarted = (planId: string) => {
    if (planId === 'economy') { navigate('/auth'); return; }
    if (!user) { navigate('/auth'); return; }
    const plan = pricingPlans.find((p) => p.id === planId);
    if (!plan) return;
    const priceValue = isAnnual && plan.annualPrice
      ? parseFloat(plan.annualPrice.replace('€', ''))
      : parseFloat(plan.monthlyPrice.replace('€', ''));
    setPendingPurchase({
      type: 'subscription',
      tier: planId,
      duration: isAnnual ? 'Annual' : 'Monthly',
      price: priceValue,
    });
    setCheckoutModalOpen(true);
  };

  const handleConfirmCheckout = async (couponCode?: string) => {
    if (!pendingPurchase) return;
    if (pendingPurchase.type === 'subscription') {
      try {
        toast({ title: 'Redirecting to payment...', description: 'Please wait while we prepare your checkout session.' });
        const referralCode = localStorage.getItem('referral_code') || undefined;
        const { data, error } = await supabase.functions.invoke('create-checkout', {
          body: {
            tier: pendingPurchase.tier,
            return_url: window.location.origin + '/dashboard',
            is_annual: isAnnual,
            referral_code: referralCode,
            coupon_code: couponCode,
          },
        });
        if (error) {
          console.error('Checkout error:', error);
          toast({ title: 'Payment Error', description: 'Unable to create checkout session. Please try again.', variant: 'destructive' });
          throw error;
        }
        if (data?.url) {
          if (referralCode) localStorage.removeItem('referral_code');
          toast({ title: 'Redirecting to Stripe...', description: "You'll be redirected to complete your payment." });
          setTimeout(() => { window.location.href = data.url; }, 500);
        } else {
          throw new Error('No checkout URL received');
        }
      } catch (error) {
        console.error('Error creating checkout:', error);
        toast({ title: 'Error', description: 'Failed to start checkout. Please try again or contact support.', variant: 'destructive' });
        throw error;
      }
    } else if (pendingPurchase.type === 'gift' && pendingPurchase.giftDetails) {
      try {
        const { data, error } = await supabase.functions.invoke('create-gift-checkout', {
          body: {
            recipientName: pendingPurchase.giftDetails.recipientName,
            recipientEmail: pendingPurchase.giftDetails.recipientEmail,
            senderName: user?.email || '',
            message: pendingPurchase.giftDetails.message,
            tier: pendingPurchase.tier,
            duration: pendingPurchase.duration,
          },
        });
        if (error) throw error;
        if (data?.url) {
          window.open(data.url, '_blank');
          toast({ title: 'Redirecting to Payment', description: 'Complete the payment to send your gift subscription.' });
          setCheckoutModalOpen(false);
          setGiftModalOpen(false);
          setGiftForm({ recipientName: '', recipientEmail: '', senderName: '', message: '' });
        }
      } catch (error) {
        console.error('Error creating gift checkout:', error);
        throw error;
      }
    } else if (pendingPurchase.type === 'boost') {
      try {
        const { data, error } = await supabase.functions.invoke('create-boost-checkout', {
          body: { tier: pendingPurchase.tier, duration: pendingPurchase.duration },
        });
        if (error) throw error;
        if (data?.url) {
          window.open(data.url, '_blank');
          toast({ title: 'Redirecting to Payment', description: 'Complete the payment to activate your boost pass.' });
          setCheckoutModalOpen(false);
        }
      } catch (error) {
        console.error('Error creating boost checkout:', error);
        throw error;
      }
    }
  };

  const handleGiftClick = (tier: 'premium_economy' | 'first_class') => {
    if (!user) { navigate('/auth'); return; }
    setGiftDetails({
      tier,
      duration: '1 Month',
      price: tier === 'premium_economy' ? '€6.99' : '€11.99',
    });
    setGiftModalOpen(true);
  };

  const handleGiftSubmit = () => {
    if (!giftForm.recipientName || !giftForm.recipientEmail || !giftForm.senderName) {
      toast({ title: 'Missing Information', description: 'Please fill in all required fields.', variant: 'destructive' });
      return;
    }
    if (!giftDetails) return;
    setPendingPurchase({
      type: 'gift',
      tier: giftDetails.tier,
      duration: giftDetails.duration,
      price: parseFloat(giftDetails.price.replace('€', '')),
      giftDetails: {
        recipientName: giftForm.recipientName,
        recipientEmail: giftForm.recipientEmail,
        message: giftForm.message,
      },
    });
    setCheckoutModalOpen(true);
  };

  const handleBoostPass = (tier: 'premium_economy' | 'first_class', duration: string, price: number) => {
    // Guests pay first — no account required up front.
    if (!user) {
      setGuestBoost({ open: true, tier, duration, price });
      return;
    }
    setPendingPurchase({ type: 'boost', tier, duration, price });
    setCheckoutModalOpen(true);
  };

  const handleStartFree = () => navigate('/auth');

  return (
    <>
      <Helmet>
        <title>OdyVoy Pricing - Travel Plans from Free to Premium</title>
        <meta name="description" content="Compare OdyVoy plans: free Economy, Premium Economy €6.99/mo, First Class €11.99/mo. Boost Passes from €2.99 for one trip. No subscription." />
        <link rel="canonical" href="https://odyvoy.com/pricing" />
        <meta property="og:title" content="OdyVoy Pricing - Travel Planning Plans Starting Free" />
        <meta property="og:description" content="Flexible pricing for every traveler. From free basic features to premium AI-powered travel assistance. Start free or upgrade anytime." />
        <meta property="og:url" content="https://odyvoy.com/pricing" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://odyvoy.com/lovable-uploads/odyvoy-logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="OdyVoy Pricing - Travel Planning Plans Starting Free" />
        <meta name="twitter:description" content="Flexible pricing for every traveler. From free basic features to premium AI-powered travel assistance." />
        <meta name="twitter:image" content="https://odyvoy.com/lovable-uploads/odyvoy-logo.png" />
        <script type="application/ld+json">{JSON.stringify(pricingStructuredData)}</script>
      </Helmet>

      <div className="pricing-page min-h-screen bg-background">
        <PricingHeader />

        <main>
          <PricingHero />
          <BoostPassesSection onBoostPass={handleBoostPass} />
          <PricingPlansSection
            isAnnual={isAnnual}
            setIsAnnual={setIsAnnual}
            hasReferralDiscount={hasReferralDiscount}
            onGetStarted={handleGetStarted}
          />
          <PricingComparison onBoostPass={handleBoostPass} onStartFree={handleStartFree} onChoosePlan={handleGetStarted} />
          <GiftSection onGiftClick={handleGiftClick} />
          <PricingFAQ />
        </main>

        <footer className="border-t border-border bg-secondary/20 pb-24 pt-12 sm:pb-16 sm:pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col items-center gap-8 sm:flex-row sm:items-start sm:justify-between sm:gap-12">
              <div className="flex flex-col items-center gap-5 sm:items-start">
                <img src={logoFull} alt="OdyVoy - Your Odyssey. Our Voyage!" className="h-10 w-auto" width={1259} height={292} loading="lazy" decoding="async" />
                <SocialFollow />
              </div>
              <p className="text-xs text-muted-foreground sm:text-sm">© 2026 OdyVoy. All rights reserved.</p>
            </div>
          </div>
        </footer>

        <GiftModal
          open={giftModalOpen}
          onOpenChange={setGiftModalOpen}
          giftDetails={giftDetails}
          setGiftDetails={setGiftDetails}
          giftForm={giftForm}
          setGiftForm={setGiftForm}
          loading={giftLoading}
          onSubmit={handleGiftSubmit}
        />

        <CheckoutConfirmationModal
          open={checkoutModalOpen}
          onOpenChange={setCheckoutModalOpen}
          purchaseType={pendingPurchase?.type || 'subscription'}
          tier={pendingPurchase?.tier || ''}
          duration={pendingPurchase?.duration}
          price={pendingPurchase?.price || 0}
          onConfirm={handleConfirmCheckout}
          giftDetails={pendingPurchase?.giftDetails}
        />

        <GuestBoostModal
          open={guestBoost.open}
          onOpenChange={(open) => setGuestBoost((prev) => ({ ...prev, open }))}
          tier={guestBoost.tier}
          duration={guestBoost.duration}
          price={guestBoost.price}
        />

        <BoostExitIntentPopup
          onGrab={() => handleBoostPass('premium_economy', '3-Day', 2.99)}
        />

        <FloatingChatButton />
      </div>
    </>
  );
};

export default Pricing;
